import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { GridDots, CornerMark, VShape, GoldenCircles } from "./GeometricDecor";
import { testimonialsContent } from "@/lib/content";
import { getLanguageFromPath } from "@/lib/language";

function Lightbox({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-10"
      style={{ backgroundColor: "rgba(0,0,0,0.85)" }}
      onClick={onClose}
    >
      <div
        className="relative max-w-5xl w-full max-h-[90vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={src}
          alt={alt}
          className="max-w-full max-h-[85vh] rounded-xl object-contain"
          style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.5)" }}
        />
        <button
          onClick={onClose}
          className="absolute -top-4 -right-4 w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer"
          style={{ backgroundColor: "#004643", color: "#acd1c6" }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#acd1c6"; (e.currentTarget as HTMLElement).style.color = "#004643"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#004643"; (e.currentTarget as HTMLElement).style.color = "#acd1c6"; }}
        >
          <i className="ri-close-line text-lg" />
        </button>
        <div
          className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-xs font-medium"
          style={{ backgroundColor: "rgba(0,70,67,0.85)", color: "#acd1c6" }}
        >
          {alt}
        </div>
      </div>
    </div>
  );
}

function AnonymousAvatar({ name, labelSuffix, avatar, size = "large", active = true }: { name: string; labelSuffix: string; avatar?: string; size?: "large" | "small"; active?: boolean }) {
  const isLarge = size === "large";

  return (
    <div
      role="img"
      aria-label={`${name} ${labelSuffix}`}
      className={`relative z-10 flex items-center justify-center rounded-full transition-all duration-300 ${
        isLarge ? "w-24 h-24 md:w-28 md:h-28" : "w-10 h-10"
      }`}
      style={{
        backgroundColor: active ? "rgba(172,209,198,0.22)" : "rgba(172,209,198,0.12)",
        border: isLarge
          ? "3px solid #acd1c6"
          : active
            ? "2px solid #004643"
            : "2px solid transparent",
        opacity: active ? 1 : 0.5,
        color: "#004643",
      }}
    >
      {avatar ? (
        <img src={avatar} alt={name} className="w-full h-full rounded-full object-cover" />
      ) : (
        <i className={isLarge ? "ri-user-line text-4xl md:text-5xl" : "ri-user-line text-lg"} />
      )}
    </div>
  );
}

function WorkGallery({ works }: { works: { image: string; label: string }[] }) {
  const [workIdx, setWorkIdx] = useState(0);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);

  const prevWork = (e: React.MouseEvent) => {
    e.stopPropagation();
    setWorkIdx((i) => (i - 1 + works.length) % works.length);
  };
  const nextWork = (e: React.MouseEvent) => {
    e.stopPropagation();
    setWorkIdx((i) => (i + 1) % works.length);
  };

  return (
    <>
      {lightbox && (
        <Lightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />
      )}
      <div className="mt-6 rounded-xl overflow-hidden" style={{ border: "1px solid rgba(172,209,198,0.25)" }}>
        {/* Image */}
        <div
          className="relative w-full cursor-zoom-in group"
          style={{ aspectRatio: "1 / 1" }}
          onClick={() => setLightbox({ src: works[workIdx].image, alt: works[workIdx].label })}
        >
          <img
            key={workIdx}
            src={works[workIdx].image}
            alt={works[workIdx].label}
            className="w-full h-full object-cover object-top transition-opacity duration-500"
          />
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

          {/* Zoom hint */}
          <div
            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            style={{ backgroundColor: "rgba(0,70,67,0.85)", color: "#acd1c6" }}
          >
            <i className="ri-zoom-in-line text-sm" />
          </div>

          {/* Label */}
          <div className="absolute bottom-3 left-4 right-16">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
              style={{ backgroundColor: "rgba(0,70,67,0.85)", color: "#acd1c6" }}
            >
              <i className="ri-image-line text-xs" />
              {works[workIdx].label}
            </span>
          </div>

          {/* Nav arrows – only show if more than 1 image */}
          {works.length > 1 && (
            <div className="absolute bottom-3 right-3 flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={prevWork}
                className="w-7 h-7 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer"
                style={{ backgroundColor: "rgba(0,70,67,0.8)", color: "#acd1c6" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#004643"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(0,70,67,0.8)"; }}
              >
                <i className="ri-arrow-left-s-line text-sm" />
              </button>
              <button
                onClick={nextWork}
                className="w-7 h-7 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer"
                style={{ backgroundColor: "rgba(0,70,67,0.8)", color: "#acd1c6" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#004643"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(0,70,67,0.8)"; }}
              >
                <i className="ri-arrow-right-s-line text-sm" />
              </button>
            </div>
          )}
        </div>

        {/* Dot indicators */}
        {works.length > 1 && (
          <div className="flex items-center justify-center gap-1.5 py-2.5" style={{ backgroundColor: "rgba(172,209,198,0.06)" }}>
            {works.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setWorkIdx(i); }}
                className="rounded-full transition-all duration-300 cursor-pointer"
                style={{
                  width: i === workIdx ? 20 : 6,
                  height: 6,
                  backgroundColor: i === workIdx ? "#004643" : "rgba(0,70,67,0.2)",
                }}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default function TestimonialsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const location = useLocation();
  const language = getLanguageFromPath(location.pathname);
  const copy = testimonialsContent[language];
  const testimonials = copy.testimonials;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal").forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = "1";
                (el as HTMLElement).style.transform = "translateY(0)";
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const goTo = (idx: number) => {
    if (isAnimating || idx === activeIdx) return;
    setIsAnimating(true);
    setActiveIdx(idx);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const next = () => goTo((activeIdx + 1) % testimonials.length);
  const prev = () => goTo((activeIdx - 1 + testimonials.length) % testimonials.length);

  const current = testimonials[activeIdx];

  return (
    <section id="testimonials" ref={sectionRef} className="py-24 md:py-32 bg-white relative overflow-hidden">
      {/* Geometric decor */}
      <GoldenCircles size={360} opacity={0.04} color="#004643" className="-top-20 -right-20" />
      <GridDots cols={7} rows={4} gap={32} opacity={0.06} color="#004643" className="right-10 bottom-20" />
      <VShape width={180} height={110} opacity={0.04} color="#004643" strokeWidth={1} className="top-16 left-1/3" />
      <CornerMark position="tr" size={28} color="#acd1c6" opacity={0.25} className="top-10 right-10" />
      <CornerMark position="bl" size={28} color="#acd1c6" opacity={0.2} className="bottom-10 left-10" />

      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div>
            <div
              className="reveal opacity-0 translate-y-6 transition-all duration-700 inline-flex items-center gap-2 px-5 py-2 rounded-full border text-xs font-medium tracking-widest uppercase mb-6"
              style={{ borderColor: "#acd1c6", color: "#004643" }}
            >
              <i className="ri-chat-quote-line text-sm" />
              {copy.eyebrow}
            </div>
            <h2
              className="reveal opacity-0 translate-y-6 transition-all duration-700 text-3xl md:text-4xl lg:text-5xl font-light leading-tight"
              style={{ fontFamily: "'League Spartan', sans-serif", color: "#004643" }}
            >
              {copy.titleTop}
              <br />
              <span className="font-bold italic">{copy.titleAccent}</span>
            </h2>
          </div>
          <p className="reveal opacity-0 translate-y-6 transition-all duration-700 text-gray-500 text-base max-w-sm lg:text-right">
            {copy.intro}
          </p>
        </div>

        {/* Main testimonial display */}
        <div className="reveal opacity-0 translate-y-6 transition-all duration-700">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">

            {/* Left: Avatar & Info */}
            <div className="lg:col-span-4 flex flex-col items-center lg:items-start text-center lg:text-left">
              <div className="relative mb-6">
                <div className="absolute inset-0 -m-3 rounded-full" style={{ border: "1px solid rgba(172,209,198,0.3)" }} />
                <div className="absolute inset-0 -m-6 rounded-full" style={{ border: "1px solid rgba(172,209,198,0.15)" }} />
                <AnonymousAvatar
                  name={current.name}
                  labelSuffix={copy.anonymousSuffix}
                  avatar={"avatar" in current ? current.avatar : undefined}
                />
                <div
                  className="absolute -bottom-2 -right-2 w-10 h-10 flex items-center justify-center rounded-full z-20"
                  style={{ backgroundColor: "#004643" }}
                >
                  <i className="ri-double-quotes-r text-sm" style={{ color: "#acd1c6" }} />
                </div>
              </div>

              <h3 className="text-lg font-semibold mb-1" style={{ color: "#004643", fontFamily: "'League Spartan', sans-serif" }}>
                {current.name}
              </h3>
              <p className="text-sm mb-1" style={{ color: "#004643", opacity: 0.7 }}>
                {current.role}
              </p>
              <p className="text-xs font-medium mb-4" style={{ color: "#acd1c6" }}>
                {current.company}
              </p>

              {/* Stars */}
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i
                    key={i}
                    className={i < current.rating ? "ri-star-fill text-sm" : "ri-star-line text-sm"}
                    style={{ color: i < current.rating ? "#acd1c6" : "rgba(0,70,67,0.2)" }}
                  />
                ))}
              </div>

              <span
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                style={{ backgroundColor: "rgba(172,209,198,0.15)", color: "#004643" }}
              >
                <i className="ri-briefcase-line text-xs" />
                {current.service}
              </span>
            </div>

            {/* Right: Quote + Work Gallery */}
            <div className="lg:col-span-8 flex flex-col gap-0">
              {/* Quote card */}
              <div
                className="relative rounded-t-2xl p-8 md:p-10"
                style={{
                  backgroundColor: "#f0f9f7",
                  border: "1px solid #e0eeeb",
                  borderBottom: "none",
                }}
              >
                <div
                  className="absolute top-4 left-6 text-6xl md:text-8xl font-bold leading-none select-none pointer-events-none"
                  style={{ color: "rgba(172,209,198,0.2)", fontFamily: "'League Spartan', sans-serif" }}
                >
                  &ldquo;
                </div>
                <p
                  className="text-base md:text-lg leading-relaxed relative z-10 pt-6"
                  style={{ color: "#004643", opacity: 0.85 }}
                >
                  {current.text}
                </p>
                <div className="mt-6 pt-6 flex items-center gap-4" style={{ borderTop: "1px solid rgba(172,209,198,0.3)" }}>
                  <div className="flex-1 h-px" style={{ backgroundColor: "rgba(172,209,198,0.2)" }} />
                  <div className="flex items-center gap-2 text-xs" style={{ color: "rgba(0,70,67,0.4)" }}>
                    <i className="ri-verified-badge-line" />
                    <span>{copy.verified}</span>
                  </div>
                  <div className="flex-1 h-px" style={{ backgroundColor: "rgba(172,209,198,0.2)" }} />
                </div>
              </div>

              {/* Work gallery – seamlessly attached below quote */}
              <div
                className="rounded-b-2xl overflow-hidden"
                style={{ border: "1px solid #e0eeeb", borderTop: "1px solid rgba(172,209,198,0.2)" }}
              >
                {/* Gallery header */}
                <div
                  className="px-6 py-3 flex items-center gap-2"
                  style={{ backgroundColor: "rgba(172,209,198,0.08)" }}
                >
                  <i className="ri-gallery-line text-xs" style={{ color: "#acd1c6" }} />
                  <span className="text-xs font-medium tracking-wide" style={{ color: "rgba(0,70,67,0.6)" }}>
                    {copy.worksTitle}
                  </span>
                </div>

                {/* Gallery body */}
                <WorkGallery key={activeIdx} works={current.works} />
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="reveal opacity-0 translate-y-6 transition-all duration-700 flex items-center justify-center gap-4 mt-12">
          <button
            onClick={prev}
            className="w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 cursor-pointer"
            style={{ border: "1px solid rgba(0,70,67,0.2)", color: "#004643" }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = "#004643";
              (e.currentTarget as HTMLElement).style.color = "#acd1c6";
              (e.currentTarget as HTMLElement).style.borderColor = "#004643";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
              (e.currentTarget as HTMLElement).style.color = "#004643";
              (e.currentTarget as HTMLElement).style.borderColor = "rgba(0,70,67,0.2)";
            }}
          >
            <i className="ri-arrow-left-line" />
          </button>

          <div className="flex items-center gap-2">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                onClick={() => goTo(idx)}
                className="transition-all duration-300 cursor-pointer rounded-full"
                style={{
                  width: idx === activeIdx ? 28 : 10,
                  height: 10,
                  backgroundColor: idx === activeIdx ? "#004643" : "rgba(0,70,67,0.2)",
                }}
              />
            ))}
          </div>

          <button
            onClick={next}
            className="w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 cursor-pointer"
            style={{ border: "1px solid rgba(0,70,67,0.2)", color: "#004643" }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = "#004643";
              (e.currentTarget as HTMLElement).style.color = "#acd1c6";
              (e.currentTarget as HTMLElement).style.borderColor = "#004643";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
              (e.currentTarget as HTMLElement).style.color = "#004643";
              (e.currentTarget as HTMLElement).style.borderColor = "rgba(0,70,67,0.2)";
            }}
          >
            <i className="ri-arrow-right-line" />
          </button>
        </div>

        {/* Thumbnail strip */}
        <div className="reveal opacity-0 translate-y-6 transition-all duration-700 flex items-center justify-center gap-3 mt-8">
          {testimonials.map((t, idx) => (
            <button
              key={t.id}
              onClick={() => goTo(idx)}
              className="relative transition-all duration-300 cursor-pointer"
            >
              <AnonymousAvatar
                name={t.name}
                labelSuffix={copy.anonymousSuffix}
                avatar={"avatar" in t ? t.avatar : undefined}
                size="small"
                active={idx === activeIdx}
              />
              {idx === activeIdx && (
                <div
                  className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: "#004643" }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Stats row */}
        <div
          className="reveal opacity-0 translate-y-6 transition-all duration-700 grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-16"
          style={{ borderTop: "1px solid rgba(172,209,198,0.3)" }}
        >
          {copy.stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div
                className="text-3xl md:text-4xl font-bold mb-2"
                style={{ fontFamily: "'League Spartan', sans-serif", color: "#004643" }}
              >
                {stat.num}
              </div>
              <div className="text-xs" style={{ color: "rgba(0,70,67,0.5)" }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
